import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// react-router's useParams() types every value as `string | undefined`, even on routes that
// guarantee a param is present. This reads the named params and, if any are unexpectedly
// missing (should never happen given the route definitions, but bookmarks/manual URL edits can
// always produce garbage), redirects to `fallback` instead of letting a page render with an
// `undefined` id and crash deeper in a fetch call. Returns null for the one render before the
// redirect effect fires — callers should render nothing in that case.
export function useRequiredParams<T extends Record<string, string>>(
  keys: (keyof T)[],
  fallback: string
): T | null {
  const params = useParams();
  const navigate = useNavigate();

  const missing = keys.some(k => !params[k as string]);

  useEffect(() => {
    if (missing) navigate(fallback, { replace: true });
  }, [missing, navigate, fallback]);

  if (missing) return null;
  return params as T;
}
